import { Sidebar } from 'primereact/sidebar';
import { useQuery } from '@tanstack/react-query';
import { ProgressSpinner } from 'primereact/progressspinner';
import ErrorMessage from '../../../components/messages/ErrorMessage';
import { fetchPolicy } from '../../../services/Insurance/Policy';
import PolicyData from './PolicyData';
import { useRef, useState } from 'react';
import { Button } from 'primereact/button';
import { FormProvider, useForm } from 'react-hook-form';
import UpdatePolicy from './UpdatePolicy';
import { Policy, PolicyForm } from '../../../Interfaces/Insurance/Policy';
import { useRolesAccessContext } from '../../../context/RolesAccessContext';
import PolicyDrilldownReport from './PolicyDrilldownReport';
import { useCompanyContext } from '@/src/app/context/CompanyContext';

type PolicyDrilldownProps = {
  visible: boolean;
  setVisible: (value: boolean) => void;
  policyID: string;
};

const PolicyDrilldown = ({
  visible,
  setVisible,
  policyID,
}: PolicyDrilldownProps) => {
  const { selectedCompany } = useCompanyContext();
  const { rolesAcess } = useRolesAccessContext();
  const [policy, setPolicy] = useState<Policy>();
  const [hideUpdate, setHideUpdate] = useState(false);
  const drilldownRef = useRef<HTMLDivElement | null>(null);
  const formMethods = useForm<PolicyForm>();
  const { setValue, reset } = formMethods;
  const [showUpdateView, setShowUpdateView] = useState(false);
  const access = rolesAcess?.find((access) => access.report === 'policy');

  const { isFetching, isError, isSuccess } = useQuery({
    queryKey: ['getPolicy', policyID],
    queryFn: ({ signal }) => fetchPolicy(policyID, signal),
    onSuccess: (data) => {
      setPolicy(data);
    },
    refetchOnWindowFocus: false,
  });

  const onUpdateShow = () => {
    if (policy) {
      setValue('policy', policy.policy);
      setValue('limit', policy.limit);
      setValue('deductible', policy.deductible);
      setValue('renewal', policy.renewal);
      setValue('termStart', new Date(policy.termStart));
      setValue('termEnd', new Date(policy.termEnd));
      setValue('insurer', policy.insurer);
      setValue('number', policy.number);
      setValue('billing', policy.billing);
      setValue('notes', policy.notes);
      setValue('removeAttachment', false);
    }
  };

  return (
    <Sidebar
      visible={visible}
      position="right"
      onHide={() => {
        setVisible(false);
        setShowUpdateView(false);
        reset();
        setHideUpdate(false);
      }}
      className="w-full max-w-600px"
    >
      {isFetching && (
        <div className="mx-8 h-full flex align-items-center">
          <ProgressSpinner />
        </div>
      )}
      {isError && !isFetching && !policy && (
        <div className="h-full flex align-items-center">
          <ErrorMessage
            content={'Failed to obtain data! Please try again later.'}
          />
        </div>
      )}
      {isSuccess && !isFetching && policy && (
        <div
          id="policy-drilldown"
          ref={drilldownRef}
          className="blackText text-standard printBackground drilldownContent"
        >
          <div className="mt-2 flex flex-column sm:row-gap-2 sm:flex-row flex-wrap align-items-center justify-content-center sm:justify-content-end">
            <div className="text-28px font-bold mx-auto sm:ml-4 drilldownHeader">
              Policy Drilldown
            </div>
            {!showUpdateView && (
              <div className="text-20px sm:mr-3 mt-2 sm:mt-0 cursor-pointer printHide">
                <PolicyDrilldownReport elementRef={drilldownRef} />
              </div>
            )}
            {access?.editable && selectedCompany?.isActive && !hideUpdate && (
              <Button
                onClick={() => {
                  if (showUpdateView) {
                    setShowUpdateView(false);
                  } else {
                    onUpdateShow();
                    setShowUpdateView(true);
                  }
                }}
                className={`w-8rem ${
                  showUpdateView ? 'bluwaiRed' : 'buttonBackground'
                } border-0 flex py-3 printHide`}
                type="button"
              >
                <span className="mx-auto text-20px text-white">
                  {showUpdateView ? 'Close' : 'Update'}
                </span>
              </Button>
            )}
          </div>
          <div className="mt-5 ml-4">
            {showUpdateView ? (
              <FormProvider {...formMethods}>
                <UpdatePolicy
                  id={policyID}
                  prevAttachment={policy.attachmentName}
                  policyType={policy.policy}
                  afterUpdate={(policy) => setPolicy(policy)}
                />
              </FormProvider>
            ) : (
              <PolicyData
                policy={policy}
                afterRemoval={() => setHideUpdate(true)}
              />
            )}
          </div>
        </div>
      )}
    </Sidebar>
  );
};

export default PolicyDrilldown;
